import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styles } from './Slideout.css';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { useTrackingContext } from '../Tracking';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useEventsContext } from '../context/Events';

export interface SlideoutProps {
  isOpen: boolean;
  size?: PossiblyResponsiveValue<'100%' | '90%' | '75%' | '67%' | '50%' | '33%' | '25%' | string>;
  minWidth?: PossiblyResponsiveValue<React.CSSProperties['minWidth']>;
  container?: Parameters<typeof createPortal>[1];
  onClose: () => void;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  backgroundColor?: 'white' | 'grey';
  anchor?: 'left' | 'right';
  dataTestId?: string;
  headerElement?: ReactNode;
}

export const Slideout = ({
  isOpen,
  size: sizeProp = { xs: '100%', sm: '75%', md: '67%', xl: '55%' },
  minWidth: minWidthProp = sizeProp,
  children,
  onClose,
  container = document.body,
  hideCloseButton = false,
  backgroundColor = 'grey',
  anchor = 'right',
  dataTestId = 'slideout',
  headerElement,
}: SlideoutProps) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const size = useResponsiveValue(sizeProp);
  const minWidth = useResponsiveValue(minWidthProp);
  const { tracker } = useTrackingContext();
  const { postEvent } = useEventsContext();
  const isRendered = useRef(false);

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (isRendered.current) {
      postEvent({
        name: isOpen ? 'slideout.open' : 'slideout.close',
        data: { slideoutName: dataTestId },
      });
    }
    isRendered.current = true;

    if (!isOpen) return;
    setShouldRender(true);
    document.addEventListener('keyup', handleKeyUp, false);
    tracker.trackImpression({ label: 'slideout' });
    return () => {
      document.removeEventListener('keyup', handleKeyUp, false);
    };
  }, [isOpen]);

  return (
    <>
      {createPortal(
        <div
          role="button"
          tabIndex={0}
          onKeyDown={() => {}}
          style={{ visibility: shouldRender ? 'visible' : 'hidden' }}
          className={styles.backgroundMask({ isOpen })}
          onClick={onClose}
          onTransitionEnd={isOpen ? undefined : () => setShouldRender(false)}
          data-testid={`${dataTestId}-background-mask`}
        >
          <div
            className={styles.slideout({ isOpen })}
            style={{
              width: `max(${size}, ${minWidth})`,
              backgroundColor: backgroundColor === 'white' ? palette.primary.light : palette.secondary.main,
              marginLeft: anchor === 'right' ? 'auto' : undefined,
            }}
            onClick={e => e.stopPropagation()}
            data-testid={dataTestId}
          >
            <div className={styles.header}>
              <div className={styles.headerElement}>{headerElement ? headerElement : null}</div>

              {!hideCloseButton ? (
                <Button
                  onClick={onClose}
                  type="iconOnly"
                  icon={<Icon name="Close" />}
                  classes={styles.closeButton}
                  data-testid={`${dataTestId}-close-button`}
                />
              ) : null}
            </div>
            {children}
          </div>
        </div>,
        container
      )}
    </>
  );
};
